const DEFAULT_SCHOOL_CODE = String(process.env.REACT_APP_SCHOOL_CODE || "JBS")
  .trim()
  .toUpperCase();

const CLASS_NAME_TO_CODE = {
  "Pre-Nursery": "PN",
  "Pre Nursery": "PN",
  "PreNursery": "PN",
  "Nursery": "NU",
  "Junior KG": "JK",
  "JuniorKG": "JK",
  "Senior KG": "SK",
  "SeniorKG": "SK",
};

const CLASS_CODES = new Set(Object.values(CLASS_NAME_TO_CODE));

const normalizeSchoolCode = (value) => {
  const token = String(value || "").trim();
  if (!token) return DEFAULT_SCHOOL_CODE;
  const cleaned = token.replace(/[^a-zA-Z0-9]/g, "");
  return cleaned ? cleaned.toUpperCase() : DEFAULT_SCHOOL_CODE;
};

const normalizeSection = (value) => {
  const token = String(value || "").trim().toUpperCase();
  if (!token) return "";
  return token.slice(0, 1);
};

const pad2 = (value) => String(value ?? "").padStart(2, "0").slice(-2);

const parseAdmissionYear = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const match = raw.match(/(20\d{2}|\d{2})/);
  if (!match) return "";
  const year = match[1];
  return year.length === 4 ? year.slice(-2) : year;
};

export const parseLegacyStudentId = (value = "") => {
  const raw = String(value || "").trim().toUpperCase();
  const match = raw.match(/^([A-Z0-9]+)-(\d{2})-([A-Z0-9]+)-(\d{4})$/);
  if (!match) return null;
  const [, schoolCode, year, classTokenRaw, rollToken] = match;
  let classToken = classTokenRaw;
  let section = "";
  if (/[A-Z]$/.test(classTokenRaw)) {
    const baseToken = classTokenRaw.slice(0, -1);
    const last = classTokenRaw.slice(-1);
    if (/^\d+$/.test(baseToken) || CLASS_CODES.has(baseToken)) {
      classToken = baseToken;
      section = last;
    }
  }
  return {
    schoolCode,
    year,
    classToken,
    section,
    rollToken,
  };
};

const resolveClassCode = ({ classValue, legacyClassToken }) => {
  const direct = String(classValue || "").trim();
  if (direct) {
    if (/^\d+$/.test(direct)) return pad2(direct);
    if (CLASS_NAME_TO_CODE[direct]) return CLASS_NAME_TO_CODE[direct];
    const upper = direct.toUpperCase();
    if (CLASS_CODES.has(upper)) return upper;
  }
  const fallback = String(legacyClassToken || "").trim();
  if (!fallback) return "";
  if (/^\d+$/.test(fallback)) return pad2(fallback);
  const upperFallback = fallback.toUpperCase();
  if (CLASS_CODES.has(upperFallback)) return upperFallback;
  return "";
};

const resolveRollToken = ({ rollNo, legacyRollToken }) => {
  const raw = String(rollNo ?? "").trim();
  if (raw && /^\d+$/.test(raw)) return pad2(raw);
  const legacy = String(legacyRollToken || "").trim();
  if (legacy && /^\d+$/.test(legacy)) return pad2(legacy.slice(-2));
  return "";
};

export const formatParentLoginId = ({
  loginId,
  legacyId,
  schoolCode,
  admissionDate,
  session,
  classValue,
  section,
  rollNo,
} = {}) => {
  const explicit = String(loginId || "").trim();
  if (explicit) return explicit;
  const legacy = parseLegacyStudentId(legacyId || "");
  const code = normalizeSchoolCode(schoolCode || legacy?.schoolCode);
  const year =
    legacy?.year ||
    parseAdmissionYear(admissionDate) ||
    parseAdmissionYear(session);
  const classCode = resolveClassCode({ classValue, legacyClassToken: legacy?.classToken });
  const sectionToken = normalizeSection(section || legacy?.section);
  const rollToken = resolveRollToken({ rollNo, legacyRollToken: legacy?.rollToken });

  if (!code || !year || !classCode || !rollToken) return "";
  return `${code}${year}${classCode}${sectionToken}${rollToken}`;
};

export const normalizeParentLoginInput = (value = "") => {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw) return "";

  const legacy = parseLegacyStudentId(raw);
  if (legacy) {
    return `${legacy.schoolCode}-${legacy.year}-${legacy.classToken}${legacy.section || ""}-${legacy.rollToken}`;
  }

  const compact = raw.replace(/[^A-Z0-9]/g, "");
  const match = compact.match(/^([A-Z]{2,6})(\d{2})(\d{2}|PN|NU|JK|SK)([A-Z]?)(\d{2})$/);
  if (!match) return raw;
  const [, school, year, classToken, section, rollToken] = match;
  const classValue = /^\d+$/.test(classToken) ? String(Number(classToken)) : classToken;
  const classWithSection = `${classValue}${section || ""}`;
  const rollLegacy = rollToken.padStart(4, "0");
  return `${school}-${year}-${classWithSection}-${rollLegacy}`;
};
